import { supabase } from "@/integrations/supabase/client";

// Progress/Construction Stages API
export const progressAPI = {
  // Get construction stages for a specific project
  async getStagesByProject(projectId: string) {
    try {
      // First try with order_index
      const { data, error } = await supabase
        .from('construction_stages')
        .select('*')
        .eq('project_id', projectId)
        .order('order_index', { ascending: true });
        
      if (error && error.message.includes('order_index does not exist')) {
        // If order_index doesn't exist, try without ordering
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('construction_stages')
          .select('*')
          .eq('project_id', projectId);
          
        if (fallbackError) {
          
          throw fallbackError;
        }
        
        return fallbackData || [];
      }
      
      if (error) {
        
        throw error;
      }
      
      return data || [];
    } catch (err) {
      
      throw err;
    }
  },

  // Create a new construction stage for a project
  async createStage(stageData: {
    project_id: string;
    name: string;
    phase: string;
    order_index: number;
    completed: boolean;
    completed_date?: Date;
    note?: string;
    photo_url?: string;
    created_by: string;
  }) {
    // Format data for the database
    const formattedData = {
      project_id: stageData.project_id,
      name: stageData.name,
      phase: stageData.phase,
      phase_color: this.getPhaseColor(stageData.phase),
      order_index: stageData.order_index,
      completed: stageData.completed,
      completed_date: stageData.completed_date ? stageData.completed_date.toISOString() : null,
      note: stageData.note,
      photo_url: stageData.photo_url,
      user_id: stageData.created_by
    };
    
    const { data, error } = await supabase
      .from('construction_stages')
      .insert(formattedData)
      .select()
      .single();
    
    if (error) {
      
      throw error;
    }
    
    return data;
  },

  // Update a construction stage
  async updateStage(
    stageId: string,
    stageData: {
      name?: string;
      phase?: string;
      order_index?: number;
      completed?: boolean;
      completed_date?: Date | null;
      note?: string;
      photo_url?: string;
    }
  ) {
    // Format the data for the database
    const formattedData: any = {
      name: stageData.name,
      phase: stageData.phase,
      order_index: stageData.order_index,
      completed: stageData.completed
    };
    
    // Handle phase color when phase is updated
    if (stageData.phase) {
      formattedData.phase_color = this.getPhaseColor(stageData.phase);
    }
    
    // Handle completed date
    if (stageData.completed !== undefined) {
      formattedData.completed_date = stageData.completed 
        ? (stageData.completed_date || new Date()).toISOString()
        : null;
    } else if (stageData.completed_date !== undefined) {
      formattedData.completed_date = stageData.completed_date
        ? stageData.completed_date.toISOString()
        : null;
    }
    
    // Handle other fields
    if (stageData.note !== undefined) formattedData.note = stageData.note;
    if (stageData.photo_url !== undefined) formattedData.photo_url = stageData.photo_url;
    
    const { data, error } = await supabase
      .from('construction_stages')
      .update(formattedData)
      .eq('id', stageId)
      .select()
      .single();
    
    if (error) {
      
      throw error;
    }
    
    return data;
  },

  // Toggle a stage's completed status
  async toggleStageComplete(stageId: string, completed: boolean) {
    const completedDate = completed ? new Date().toISOString() : null;
    
    const { data, error } = await supabase
      .from('construction_stages')
      .update({
        completed: completed,
        completed_date: completedDate
      })
      .eq('id', stageId)
      .select()
      .single();
    
    if (error) {
      
      throw error;
    }
    
    return data;
  },

  // Add a note to a stage
  async addStageNote(stageId: string, note: string) {
    const { data, error } = await supabase
      .from('construction_stages')
      .update({ note })
      .eq('id', stageId)
      .select()
      .single();
    
    if (error) {
      
      throw error;
    }
    
    return data;
  },

  // Update project progress based on completed stages
  async updateProjectProgress(projectId: string) {
    // Get all stages for the project
    const { data: stages, error } = await supabase
      .from('construction_stages')
      .select('completed')
      .eq('project_id', projectId);
    
    if (error) {
      
      throw error;
    }
    
    // Calculate progress percentage
    const totalStages = stages.length;
    if (totalStages === 0) return 0;
    
    const completedStages = stages.filter(stage => stage.completed).length;
    const progress = Math.round((completedStages / totalStages) * 100);
    
    // Update the project progress
    const { error: updateError } = await supabase
      .from('projects')
      .update({ progress })
      .eq('id', projectId);
    
    if (updateError) {
      
      throw updateError;
    }
    
    return progress;
  },

  // Helper function to get phase color
  getPhaseColor(phase: string): string {
    switch (phase.toLowerCase()) {
      case 'zemin hazırlığı':
        return 'bg-blue-500';
      case 'temel işleri':
        return 'bg-indigo-500';
      case 'duvarlar ve çatı':
        return 'bg-amber-800';
      case 'tesisat işleri':
        return 'bg-green-500';
      case 'iç mekan işleri':
        return 'bg-yellow-500';
      case 'son sistemler ve bitirme':
        return 'bg-red-500';
      case 'tamamlama ve teslim':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  }
}; 